import { ComposerNoteImageLocalization } from './learndb';

export type NoteType = 'pdf-note' | 'composer-note' | 'decision-making-tree';
export type ItemType = 'anatomy' | 'video' | 'quiz' | 'answers' | NoteType;
export type SavedItemType = 'video' | 'quiz' | NoteType;

export type CitationProps = {
  name: string;
  type: string;
};

export type SectionProps = {
  heading?: string;
  subheading?: string;
  text?: string;
  caption?: string;
  image?: string;
  imageAltText?: string;
};

export type HtmlNotesProps = {
  theme: string;
  title: string;
  subtitle: string;
  width: string;
  alignment: string;
  image: string;
  altText?: string;
  anchor?: string;
  titleImage?: string;
  citations?: CitationProps[];
  sections?: SectionProps[];
  imageLocalizations?: ComposerNoteImageLocalization[];
  titleImageLocalizations?: ComposerNoteImageLocalization[];
};

export enum ComposerNotesDataType {
  DMT = 'decision-making-tree',
  NOTE = 'note',
  OMM = 'OMM',
}

/**
 * Determines whether a composer note (or any content) that declares
 * `programs` visibility should be shown to a user on `userProgram`.
 *
 * - No restriction (`programs` empty/absent) → visible to everyone.
 * - Otherwise visible only when the user's program alias is listed.
 *
 * Used by the Learn page (client, via the user context) and the print route
 * (server, via the resolved user) so the gating rule lives in one place.
 * @param programs Program aliases (e.g. 'DO', 'RN') allowed to see the note.
 * @param userProgram The current user's program alias, if any.
 */
export const isVisibleForProgram = (
  programs: string[] | undefined,
  userProgram: string | undefined,
): boolean =>
  !programs?.length || Boolean(userProgram && programs.includes(userProgram));

/**
 * Programs an OMM note falls back to when an author selects the OMM type but
 * leaves `programs` unset. This is a reliability floor so Osteopathic
 * Considerations content is never accidentally published to non-DO users.
 */
export const OMM_DEFAULT_PROGRAMS = ['DO'];

/**
 * Resolves the effective visibility programs for a composer note. Author-set
 * `programs` win; otherwise an OMM note defaults to {@link OMM_DEFAULT_PROGRAMS}
 * and every other type stays unrestricted. Apply this at every read boundary
 * (learn data assembly, print route) so gating does not depend on the author
 * remembering to tick the DO checkbox.
 * @param type The note's `type` value.
 * @param programs The author-selected program aliases, if any.
 */
export const getEffectiveNotePrograms = (
  type: string | undefined,
  programs: string[] | undefined,
): string[] | undefined => {
  if (programs?.length) {
    return programs;
  }
  return type === ComposerNotesDataType.OMM ? OMM_DEFAULT_PROGRAMS : programs;
};

/**
 * Orders composer notes so OMM/Osteopathic Considerations notes come after the
 * notes everyone sees. Content authors place the Osteopathic Considerations
 * note below the existing note (or below the DMT on Clinical Sciences pages),
 * so it renders last. Order within each group is preserved.
 * @param notes The notes to order.
 */
export const orderComposerNotesOmmLast = <T extends { type?: string }>(
  notes: T[],
): T[] => [
  ...notes.filter((note) => note.type !== ComposerNotesDataType.OMM),
  ...notes.filter((note) => note.type === ComposerNotesDataType.OMM),
];

/**
 * The groups a node's composer notes are split into on the Learn page: the
 * notes everyone sees get one tab, the Osteopathic Considerations note gets its
 * own. Doubles as the `group` query param of the print route so a tab prints
 * only its own note.
 */
export enum ComposerNoteGroup {
  /** A shared note, or a Decision-Making Tree on Clinical Sciences pages. */
  STANDARD = 'standard',
  /** Osteopathic Considerations (OMM) notes, shown to DO users. */
  OMM = 'omm',
}

/**
 * Narrows composer notes to one {@link ComposerNoteGroup}. An absent or
 * unrecognised group returns every note, which keeps the stacked (non-tabbed)
 * layout and existing print URLs showing the full set.
 * @param notes The notes to narrow.
 * @param group The group to keep, if any.
 */
export const filterComposerNotesByGroup = <T extends { type?: string }>(
  notes: T[],
  group?: string,
): T[] => {
  if (group === ComposerNoteGroup.OMM) {
    return notes.filter((note) => note.type === ComposerNotesDataType.OMM);
  }
  if (group === ComposerNoteGroup.STANDARD) {
    return notes.filter((note) => note.type !== ComposerNotesDataType.OMM);
  }
  return notes;
};

/**
 * Selects the composer notes to render when a learn node has more than one
 * (e.g. a regular note or DMT plus an OMM/Osteopathic Considerations note).
 * Every note visible to the user's program is returned, OMM last, so a DO user
 * sees the shared note *and* the osteopathic one rather than one replacing the
 * other. Returns an empty array when nothing is visible.
 *
 * Filtering happens here — at the boundary where the user's program is known —
 * because the Learn data is assembled user-agnostically and cached without a
 * user, so notes cannot be filtered server-side without either hiding content
 * from non-DO users or leaking DO-only content to them.
 * @param notes The candidate notes for the node.
 * @param userProgram The current user's program alias, if any.
 */
export const selectComposerNotesForProgram = <
  T extends { type?: string; programs?: string[] },
>(
  notes: T[] | undefined,
  userProgram: string | undefined,
): T[] =>
  orderComposerNotesOmmLast(
    (notes ?? []).filter((note) =>
      isVisibleForProgram(
        getEffectiveNotePrograms(note.type, note.programs),
        userProgram,
      ),
    ),
  );

// export type ComposerNotesArgsProps = HtmlNotesProps & {
//   type: ComposerNotesDataType;
// };

// export type ComposerPageDataProps = {
//   type?: ComposerNotesDataType,
//   nextjs: boolean;
//   learnPage: string;
//   title: string;
//   image: string;
//   description: string;
//   content: {
//     path: string;
//     blocks: ComposerBlock[];
//   };
//   neeedVisitorType?: boolean;
// };

export type ComposerNotesPageProps = {
  nodeID: number;
  url: string;
  data: {
    title: string;
    learnPage: string;
    type?: ComposerNotesDataType;
    /**
     * Program aliases (e.g. 'DO', 'RN') allowed to see this note. Empty/absent
     * means visible to everyone. Sibling of `content.blocks[].args`.
     */
    programs?: string[];
    content: {
      blocks: {
        block: 'notesComposerBlock';
        args: HtmlNotesProps;
      }[];
    };
  };
};
/** END MOBILE */

export type DataBlocksNotesArgsPage = {
  primary?: {
    name: string;
    notes: {
      [key: string]: {
        doc: number;
        page: string;
      };
    };
  };
};

export type DataBlocksNotesPage = {
  svg: string;
  jpg: string;
  num: number;
  text: string;
  altText: string;
};

export type DataBlocksNotes =
  | {
      pages: DataBlocksNotesPage[][];
      focusPageNum: number;
      testVersion: string;
      pageCount: number;
      notesLink: string;
    }
  | { empty: boolean };

export interface NotesCacheEntity {
  title: string;
  nodeID: number;
  $?: string;
  svg?: string;
  contents: NotesContent[];
  flaggedPages?: string[];
  pageCount?: number;
}

export interface NotesContent {
  learnPage: string;
  pageNum: string;
  thumbnail?: string;
  duration: string;
}

export interface NotesPage {
  id?: number;
  svg: string;
  png: string;
  $?: string;
  jpg: string;
  flaggedPages?: number[];
  num: number;
  text?: string;
  altText?: string;
}

export interface NotesDataBlock {
  name?: string;
  pages?: NotesPage[];
  pageCount?: number;
  contents?: NotesContent[];
}
